import type React from "react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Menu,
  MenuItem,
  IconButton,
  Pagination,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  FormControl,
  InputLabel,
  type SelectChangeEvent,
} from "@mui/material";
import { HiDotsVertical } from "react-icons/hi";
import toast from "react-hot-toast";
import TableSkeleton from "../../common/TableSkeleton";
import { Query } from "../../common/type";
import QuerySubmitDetails from "../../common/QuerySubmitDetails";

export default function AdminTable() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const userId = user?._id;
  const accessToken = user?.accessToken;
  const [loading, setLoading] = useState(false);
  const [queries, setQueries] = useState<Query[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<Query | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");

  const [selectedQueryDetails, setSelectedQueryDetails] =
    useState<Query | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [filterStatus, setFilterStatus] = useState<"pending" | "responded">(
    "pending"
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, query: Query) => {
      setAnchorEl(event.currentTarget);
      setSelectedQuery(query);
    },
    []
  );

  const fetchQueries = useCallback(async () => {
    if (!accessToken) {
      toast.error("User authentication required");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/query/get-all-query`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (res.status === 200) {
        const data = res?.data?.data;
        if (Array.isArray(data)) {
          let allQuestions: Query[] = [];
          data.forEach((q) => {
            allQuestions = [...allQuestions, ...q.questions];
          });
          setQueries(allQuestions);
        } else {
          console.error("Data is not an array:", data);
        }
      } else {
        console.error("Error with response:", res);
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
      toast.error("Failed to fetch queries");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (userId && accessToken) {
      fetchQueries();
    }
  }, [userId, accessToken, fetchQueries]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewQuery = (query: Query) => {
    setSelectedQueryDetails(query);
    setIsModalOpen(true);
    handleClose();
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  // Filter and sort queries based on status and date
  const getFilteredQueries = () => {
    let filtered = queries.filter((query) => {
      if (filterStatus === "pending") {
        return query.status === "pending";
      } else {
        return query.status === "responded";
      }
    });

    // Apply date filter
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    switch (dateFilter) {
      case "today":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate.toDateString() === today.toDateString();
        });
        break;
      case "yesterday":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate.toDateString() === yesterday.toDateString();
        });
        break;
      case "lastWeek":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate >= lastWeek && queryDate <= today;
        });
        break;
      case "lastMonth":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate >= lastMonth && queryDate <= today;
        });
        break;
      case "oldest":
        filtered.sort(
          (a, b) =>
            new Date(a.submittedDate).getTime() -
            new Date(b.submittedDate).getTime()
        );
        break;
      case "newest":
        filtered.sort(
          (a, b) =>
            new Date(b.submittedDate).getTime() -
            new Date(a.submittedDate).getTime()
        );
        break;
      default:
        // 'all' - no additional filtering needed
        break;
    }

    return filtered;
  };

  const filteredQueries = getFilteredQueries();

  // Paginate the filtered queries
  const paginatedQueries = filteredQueries.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <>
      <div className="home px-2 sm:px-8 min-h-screen">
        <div className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <FormControl
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 200 },
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  "& fieldset": {
                    borderColor: "#1E3359",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1E3359",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1E3359",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiSelect-icon": {
                  color: "white",
                },
              }}
            >
              <InputLabel id="date-filter-label">Date Filter</InputLabel>
              <Select
                labelId="date-filter-label"
                value={dateFilter}
                label="Date Filter"
                onChange={handleDateFilterChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "white",
                      "& .MuiMenuItem-root": {
                        color: "black",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#e0e0e0",
                          "&:hover": {
                            backgroundColor: "#d5d5d5",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="lastWeek">Last Week</MenuItem>
                <MenuItem value="lastMonth">Last Month</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
              </Select>
            </FormControl>

            <div className="w-full sm:w-auto flex justify-center sm:justify-end">
              <ToggleButtonGroup
                value={filterStatus}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setFilterStatus(newValue);
                    setPage(1);
                  }
                }}
                aria-label="query status filter"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiToggleButton-root": {
                    flex: { xs: 1, sm: "none" },
                    color: "white",
                    borderColor: "#1E3359",
                    "&.Mui-selected": {
                      backgroundColor: "#1E3359",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#16304f",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#16304f",
                    },
                  },
                }}
              >
                <ToggleButton
                  value="pending"
                  aria-label="pending queries"
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                  }}
                >
                  Pending
                </ToggleButton>
                <ToggleButton
                  value="responded"
                  aria-label="responded queries"
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                  }}
                >
                  Responded
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>

          {loading ? (
            <TableSkeleton />
          ) : (
            <div className="relative flex flex-col w-full h-full rounded-[16px]">
              <div className="rounded-tl-[16px] rounded-tr-[16px] overflow-x-auto">
                <div>
                  <table className="w-full table-auto min-w-max text-center custom_table">
                    <thead className="bg-[#1E3359]">
                      <tr>
                        <th className="p-4 border-b border-slate-600">
                          <p className="text-base font-bold leading-none text-white">
                            S.No
                          </p>
                        </th>
                        <th className="p-4 border-b border-slate-600">
                          <p className="text-base font-bold leading-none text-white">
                            Question Text
                          </p>
                        </th>
                        <th className="p-4 border-b border-slate-600 hidden md:table-cell">
                          <p className="text-base font-bold leading-none text-white">
                            Status
                          </p>
                        </th>
                        <th className="p-4 border-b border-slate-600 hidden md:table-cell">
                          <p className="text-base font-bold leading-none text-white">
                            Date Submitted
                          </p>
                        </th>
                        <th className="p-4 border-b border-slate-600">
                          <p className="text-base font-bold leading-none text-white">
                            Action
                          </p>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedQueries.length > 0 ? (
                        paginatedQueries.map((query, index) => (
                          <tr key={query._id} className="hover:bg-slate-700">
                            <td className="p-4 border-b">
                              <p className="text-sm text-white font-normal">
                                {(page - 1) * rowsPerPage + index + 1}
                              </p>
                            </td>
                            <td className="p-4 border-b">
                              <p className="text-sm text-white font-normal">
                                {query?.questionText?.slice(0, 12)}
                                {query?.questionText?.length > 12 ? "..." : ""}
                              </p>
                            </td>
                            <td className="p-4 border-b hidden md:table-cell">
                              <p className="text-sm text-white font-normal">
                                {query.status}
                              </p>
                            </td>
                            <td className="p-4 border-b hidden md:table-cell">
                              <p className="text-sm text-white font-normal">
                                {new Date(
                                  query.submittedDate
                                ).toLocaleDateString()}
                              </p>
                            </td>
                            <td className="p-4 border-b text-center">
                              <IconButton
                                onClick={(e) => handleClick(e, query)}
                                className="text-white"
                              >
                                <HiDotsVertical className="cursor-pointer text-white" />
                              </IconButton>

                              <Menu
                                anchorEl={anchorEl}
                                open={
                                  Boolean(anchorEl) &&
                                  selectedQuery?._id === query._id
                                }
                                onClose={handleClose}
                              >
                                <MenuItem
                                  onClick={() => handleViewQuery(query)}
                                  sx={{
                                    color: "#fff",
                                    backgroundColor: "#1E3359",
                                    "&:hover": { backgroundColor: "#16304f" },
                                  }}
                                >
                                  View Query
                                </MenuItem>
                              </Menu>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="p-4 text-white">
                            No queries found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-center items-center py-4 text-white">
                <Pagination
                  count={Math.ceil(filteredQueries.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "white",
                    },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#1E3359",
                      color: "white",
                    },
                    "& .MuiPaginationItem-ellipsis, & .MuiPaginationItem-icon":
                      {
                        color: "white",
                      },
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <QuerySubmitDetails
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          query={selectedQuery}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchQueries();
          }}
        />
      </div>
    </>
  );
}
